import * as React from "react";
import {
  Box,
  Card,
  CardContent,
  IconButton,
  Paper,
  SxProps,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Theme,
  Typography
} from "@mui/material";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { AppBadge } from "./components/AppBadge";
import { AppButton } from "./components/AppButton";
import { AppCard } from "./components/AppCard";
import { AppIconTile } from "./components/AppIconTile";
import { AppSection } from "./components/AppSection";
import { AppTabs } from "./components/AppTabs";
import { EmptyState } from "./components/EmptyState";
import { ManageList } from "./components/ManageList";
import { PageHeader } from "./components/PageHeader";
import { RowName } from "./components/RowName";
import { SimpleCard } from "./components/SimpleCard";
import { SummaryBand } from "./components/SummaryBand";
import { bkdp } from "./theme";

export { AppBadge, AppButton, AppCard, AppIconTile, AppSection, AppTabs, EmptyState, ManageList, PageHeader, RowName, SimpleCard, SummaryBand };
export type { RowNameProps } from "./components/RowName";
export type { AppIconTileProps } from "./components/AppIconTile";
export type { AppTabGroup, AppTabItem } from "./components/AppTabs";
export type { SummaryBandData, SummaryBandGap, SummaryBandValue } from "./components/SummaryBand";

export function SectionCard({
  eyebrow,
  title,
  children
}: {
  eyebrow?: React.ReactNode;
  title: string;
  children: React.ReactNode;
}) {
  return (
    <Card>
      <CardContent sx={{ px: 4, py: 3.5 }}>
        {eyebrow && (
          <Typography
            variant="overline"
            sx={{ color: bkdp.signalBlue, display: "block", mb: 1.25 }}
          >
            {eyebrow}
          </Typography>
        )}
        <Typography variant="h6" sx={{ mb: 2.5, color: bkdp.deepNavy }}>
          {title}
        </Typography>
        {children}
      </CardContent>
    </Card>
  );
}

export type DataTableColumn = {
  key: string;
  label: React.ReactNode;
  /** Set false to leave one column unsortable when the table is sortable (the row-actions column, which has no value). */
  sortable?: boolean;
  /** Right-align a number column. */
  align?: "left" | "right";
  /** A fixed width, for a narrow column. */
  width?: number | string;
  /** The one column that takes the width the others leave: the name or the question, so a row stays one line where it can. */
  fill?: boolean;
  /** A category or a number: one line, never wrapped, so the fill column gets the rest. */
  nowrap?: boolean;
  /** Leave the column out when every row's cell is empty (a State column with nothing to act on says nothing). */
  hideWhenEmpty?: boolean;
};
export type DataTableSort = { key: string; dir: "asc" | "desc" } | null;
export type DataTableRow = Record<string, React.ReactNode>;

/**
 * Derive something sortable from a cell. Primitives sort as themselves (numeric
 * strings such as "12,345", "$1,200" or "37.5%" as numbers); a component with a
 * numeric `value` prop (Money) or a `sortValue` prop (Sorted, a fixed set in its
 * own order) sorts by it; otherwise by its text.
 */
const sortValueOf = (node: React.ReactNode): number | string => {
  if (node === null || node === undefined || typeof node === "boolean") return "";
  if (typeof node === "number") return node;
  if (typeof node === "string") {
    const cleaned = node.replace(/[$,%\s]/g, "").replace(/[–—]/g, "");
    return cleaned !== "" && !Number.isNaN(Number(cleaned)) ? Number(cleaned) : node.toLowerCase();
  }
  if (Array.isArray(node)) return sortValueOf(node[0]);
  if (React.isValidElement(node)) {
    const props = node.props as Record<string, unknown>;
    if (typeof props.sortValue === "string" || typeof props.sortValue === "number") return props.sortValue;
    if (typeof props.value === "number") return props.value;
    if (props.value === null) return Number.NEGATIVE_INFINITY;
    if (typeof props.label === "string") return props.label.toLowerCase();
    if (typeof props.title === "string" && props.children === undefined) return props.title.toLowerCase();
    return sortValueOf(props.children as React.ReactNode);
  }
  return "";
};

/** A cell whose sort position is not its text: a value from a fixed set sorts in the set's own order, the exception first. */
export function Sorted({ sortValue, children }: { sortValue: number | string; children: React.ReactNode }) { void sortValue; return <>{children}</>; }

/**
 * Optional per-row expansion: the row's children, shown beneath it. The table owns the open state unless the screen
 * keeps it (`openKeys`/`onToggle`, so it can live in the address), and renders the toggle, so every expandable table
 * in the portal announces itself the same way: one button per row carrying `aria-expanded` and the accessible name
 * `label` returns. `render` returning null leaves that row without a toggle, and the space is held so names line up.
 *
 * The chevron means children (2026-10-14): what it opens is more rows of the same kind, in the same table, indented
 * (`rows` returning child rows), or a block beneath the row (`render`). Detail about a row is not a child of it and
 * does not go here; it goes in a selected-row panel beside the table.
 */
export type DataTableExpansion = {
  /** Child rows of the same kind, rendered in the same columns, indented. */
  rows?: (row: DataTableRow, index: number) => DataTableRow[] | null;
  /** A block beneath the row (a list that is not rows of the same kind). */
  render?: (row: DataTableRow, index: number) => React.ReactNode;
  label: (row: DataTableRow, index: number, expanded: boolean) => string;
  /** The open rows' keys when the screen keeps them (in the address, say). Needs `rowAttrs` to give each row a key. */
  openKeys?: ReadonlySet<string>;
  onToggle?: (key: string, open: boolean) => void;
};

/**
 * Optional row selection: clicking anywhere on a row (not on a control in it) selects it, one at a time; Arrow Up and
 * Arrow Down move the selection through the rows as shown. The selected row is marked by a tint and a left edge bar
 * and carries `aria-selected`. The screen keeps the key; a detail panel beside the table follows it.
 */
export type DataTableSelection = { selectedKey: string | null; onSelect: (key: string | null) => void; /** The row's accessible name, for the row's label. */ label?: (row: DataTableRow) => string };

const keyOf = (attrs: { key?: string | number } | undefined, fallback: string) => (attrs?.key !== undefined ? String(attrs.key) : fallback);
const sortRows = (rows: { row: DataTableRow; index: number }[], sort: DataTableSort) => {
  if (!sort) return rows;
  const withValue = rows.map((r) => ({ ...r, v: sortValueOf(r.row[sort.key]) }));
  withValue.sort((a, b) => {
    const av = a.v, bv = b.v;
    let c = 0;
    if (typeof av === "number" && typeof bv === "number") c = av - bv;
    else if (typeof av === "number") c = -1;
    else if (typeof bv === "number") c = 1;
    else c = String(av).localeCompare(String(bv));
    if (c === 0) c = a.index - b.index;
    return sort.dir === "asc" ? c : -c;
  });
  return withValue;
};

/**
 * The one table for every list of typed things in the portal (the row rule, 2026-10-14). A real `<table>`: every
 * header is a `<th scope="col">`; a sortable header holds a button, and the sorted column carries `aria-sort` on
 * the header and a glyph beside its label, so the sort is never carried by colour alone. A category (lens, type,
 * origin, status) is a column of plain text: the header says what kind of value the column carries once, for every
 * row, so no cell needs a chip to say it. Colour is spent on one thing only, a state someone should act on, through
 * `RowState`. The table scrolls inside its own box at a narrow width; the page never scrolls sideways.
 */
export function DataTable({
  columns,
  rows,
  maxHeight,
  stickyHeader = false,
  sortable = true,
  sort: controlledSort,
  onSortChange,
  containerSx,
  expansion,
  selection,
  rowAttrs,
  caption
}: {
  columns: DataTableColumn[];
  rows: DataTableRow[];
  maxHeight?: number | string;
  stickyHeader?: boolean;
  /** Click a heading to sort by that column, again to reverse, a third time to clear. */
  sortable?: boolean;
  /** The sort, when the screen keeps it (in the URL, say) rather than the table. */
  sort?: DataTableSort;
  onSortChange?: (sort: DataTableSort) => void;
  containerSx?: SxProps<Theme>;
  expansion?: DataTableExpansion;
  selection?: DataTableSelection;
  /** Attributes for a row's `<tr>`: a key, a data-testid, data-* marks a test reads. The row's index in `rows` is the one before sorting. */
  rowAttrs?: (row: DataTableRow, index: number) => Record<string, unknown> & { key?: string | number };
  /** A visually hidden caption, read by assistive technology as the table's name. */
  caption?: string;
}) {
  const [ownSort, setOwnSort] = React.useState<DataTableSort>(null);
  const [ownOpen, setOwnOpen] = React.useState<Set<string>>(new Set());
  const sort = controlledSort !== undefined ? controlledSort : ownSort;
  const setSort = (next: DataTableSort) => { if (onSortChange) onSortChange(next); if (controlledSort === undefined) setOwnSort(next); };
  const openKeys = expansion?.openKeys ?? ownOpen;
  const toggleOpen = (key: string) => { const open = !openKeys.has(key); if (expansion?.onToggle) expansion.onToggle(key, open); if (!expansion?.openKeys) setOwnOpen((prev) => { const n = new Set(prev); if (open) n.add(key); else n.delete(key); return n; }); };
  const empty = (v: React.ReactNode): boolean => v === null || v === undefined || v === "" || (React.isValidElement(v) && (v.props as { children?: React.ReactNode }).children === undefined && (v.props as { label?: unknown }).label === undefined);
  columns = columns.filter((c) => !c.hideWhenEmpty || rows.some((r) => !empty(r[c.key])));
  const sorted = React.useMemo(() => sortRows(rows.map((row, index) => ({ row, index })), sort), [rows, sort]);
  const toggle = (key: string) => { setSort(sort?.key === key ? (sort.dir === "asc" ? { key, dir: "desc" } : null) : { key, dir: "asc" }); };
  // The rows as shown, children under their open parent, flat, so the arrow keys walk what the eye sees.
  type Shown = { row: DataTableRow; index: number; key: string; depth: number; attrs: Record<string, unknown>; children: DataTableRow[] | null; block: React.ReactNode };
  const shown: Shown[] = [];
  for (const { row, index } of sorted) {
    const { key: k, ...attrs } = rowAttrs?.(row, index) ?? {};
    const key = keyOf({ key: k }, String(index));
    const children = expansion?.rows?.(row, index) ?? null;
    const block = expansion?.render?.(row, index) ?? null;
    shown.push({ row, index, key, depth: 0, attrs, children, block });
    if (children && openKeys.has(key)) {
      for (const { row: c, index: ci } of sortRows(children.map((cr, i) => ({ row: cr, index: i })), sort)) {
        const { key: ck, ...cattrs } = rowAttrs?.(c, -1 - ci) ?? {};
        shown.push({ row: c, index: -1 - ci, key: keyOf({ key: ck }, `${key}/${ci}`), depth: 1, attrs: cattrs, children: null, block: null });
      }
    }
  }
  const onKey = (e: React.KeyboardEvent) => {
    if (!selection || (e.key !== "ArrowDown" && e.key !== "ArrowUp")) return;
    const at = shown.findIndex((r) => r.key === selection.selectedKey);
    const next = e.key === "ArrowDown" ? Math.min(shown.length - 1, at + 1) : Math.max(0, at < 0 ? 0 : at - 1);
    if (shown[next]) { e.preventDefault(); selection.onSelect(shown[next].key); }
  };
  const isControl = (t: EventTarget | null) => t instanceof Element && Boolean(t.closest("button, a, input, select, textarea, [role=menu], [role=menuitem]"));

  return (
    <Box sx={{ width: "100%", overflowX: "auto" }}>
      <TableContainer
        component={Paper}
        sx={{ maxHeight, width: "100%", ...containerSx }}
      >
        <Table size="small" stickyHeader={stickyHeader} onKeyDown={onKey}>
          {caption && <caption style={{ position: "absolute", width: 1, height: 1, overflow: "hidden", clip: "rect(0 0 0 0)", whiteSpace: "nowrap" }}>{caption}</caption>}
          <TableHead>
            <TableRow>
              {expansion && <TableCell sx={{ width: 44 }} />}
              {columns.map((column) => {
                const canSort = sortable && column.sortable !== false;
                const active = sort?.key === column.key;
                const glyph = active ? (sort!.dir === "asc" ? "▲" : "▼") : "△";
                return (
                  <TableCell
                    key={column.key}
                    scope="col"
                    align={column.align ?? "left"}
                    aria-sort={active ? (sort!.dir === "asc" ? "ascending" : "descending") : canSort ? "none" : undefined}
                    sx={{ whiteSpace: column.fill ? "nowrap" : "normal", lineHeight: 1.3, width: column.fill ? "100%" : column.width, fontWeight: active ? 700 : undefined, px: 1.5 }}
                  >
                    {canSort ? (
                      <Box
                        component="button"
                        type="button"
                        onClick={() => toggle(column.key)}
                        aria-label={`Sort by ${typeof column.label === "string" ? column.label : column.key}`}
                        sx={{ all: "unset", cursor: "pointer", display: "inline-flex", alignItems: "center", gap: 0.5, font: "inherit", color: "inherit", "&:hover": { color: bkdp.deepNavy }, "&:focus-visible": { outline: `2px solid ${bkdp.signalBlue}`, outlineOffset: 2, borderRadius: 2 } }}
                      >
                        {column.label}
                        <Box component="span" aria-hidden sx={{ fontSize: 9, lineHeight: 1, opacity: active ? 1 : 0.35 }}>{glyph}</Box>
                      </Box>
                    ) : column.label}
                  </TableCell>
                );
              })}
            </TableRow>
          </TableHead>
          <TableBody>
            {shown.map(({ row, index, key, depth, attrs, children, block }) => {
              const expanded = openKeys.has(key);
              const canExpand = (children !== null && children.length > 0) || block !== null;
              const selected = selection ? selection.selectedKey === key : undefined;
              return (
                <React.Fragment key={key}>
                  <TableRow
                    hover
                    {...attrs}
                    data-depth={depth || undefined}
                    aria-selected={selection ? Boolean(selected) : undefined}
                    aria-label={selection?.label ? selection.label(row) : undefined}
                    tabIndex={selection ? 0 : undefined}
                    onClick={selection ? (e) => { if (!isControl(e.target)) selection.onSelect(key); } : undefined}
                    sx={selection ? { cursor: "pointer", ...(selected ? { bgcolor: "action.selected", "& > td:first-of-type": { boxShadow: `inset 3px 0 0 ${bkdp.signalBlue}` } } : {}), "&:focus-visible": { outline: `2px solid ${bkdp.signalBlue}`, outlineOffset: -2 } } : undefined}
                  >
                    {expansion && (
                      <TableCell sx={{ width: 44, pr: 0 }}>
                        {canExpand && (
                          <IconButton
                            size="small"
                            aria-expanded={expanded}
                            aria-label={expansion.label(row, index, expanded)}
                            onClick={(e) => { e.stopPropagation(); toggleOpen(key); }}
                          >
                            {expanded ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
                          </IconButton>
                        )}
                      </TableCell>
                    )}
                    {columns.map((column, ci) => (
                      <TableCell key={column.key} align={column.align ?? "left"} sx={{ px: 1.5, ...(ci === 0 && depth ? { pl: 1.5 + depth * 3 } : {}), ...(column.align === "right" ? { fontVariantNumeric: "tabular-nums" } : {}), ...(column.nowrap ? { whiteSpace: "nowrap" } : {}), ...(column.width ? { width: column.width } : {}) }}>{row[column.key]}</TableCell>
                    ))}
                  </TableRow>
                  {expansion && expanded && block !== null && (
                    <TableRow>
                      <TableCell colSpan={columns.length + 1} sx={{ bgcolor: "action.hover", py: 2 }}>{block}</TableCell>
                    </TableRow>
                  )}
                </React.Fragment>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
}

/**
 * The one coloured mark a row may carry: a state someone should act on (retired, overdue, rejected, withdrawn).
 * Everything else a State column says is plain text, and a row with nothing to say leaves the cell empty. One colour,
 * one place: a screen never paints a state itself.
 */
export function RowState({ label, acting = false, title }: { label: string | null | undefined; acting?: boolean; title?: string }) {
  if (!label) return null;
  return (
    <Typography component="span" variant="body2" title={title} data-testid="row-state" data-acting={acting ? "true" : undefined} sx={acting ? { color: bkdp.amber, fontWeight: 600 } : undefined}>
      {label}
    </Typography>
  );
}
